import { Component, computed, inject } from '@angular/core';
import { IWizardStepComponent } from '@mwe/models';
import { MeterPointChangeSignalState } from '../meterpoint-change-request.types';
import { SignalState } from '@shared-ikp/service/signal-state-service/signal-state.service';
import { UiComponentsModule } from '@mwe/ui/components';
import { ProductSelectionComponent } from './components/product-selection/product-selection.component';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MeterPoint } from '../../../model/meter-point.model';
import { getAddressStreetAndPostCodeFormatted } from '@mwe/utils';
import { Router } from '@angular/router';
import { checkPrevDataAvailable, closeIfInactive } from '../meter-point-change-request.utils';
import { ROUTE_CHANGE_TYPE, ROUTE_METER_POINT_CHANGE_REQUEST } from '../../../app.constants';
import { MeterPointSelection, SELECTED_METER_POINT_PRODUCTS } from '../../../model/product-selection.model';
import { ProductCategory } from '../../../model/product-category.model';

@Component({
  selector: 'ikp-product-and-date',
  standalone: true,
  templateUrl: './product-and-date.component.html',
  imports: [UiComponentsModule, ProductSelectionComponent, ReactiveFormsModule],
})
export class ProductAndDateComponent implements IWizardStepComponent {
  protected router = inject(Router);
  protected formBuilder = inject(FormBuilder);
  protected wizardStore: MeterPointChangeSignalState = inject(SignalState);

  protected form: FormGroup = this.formBuilder.group({});

  protected hasPrevStepData = computed(
    () => this.wizardStore.state().stepChangeType !== null && this.wizardStore.state().meterPoint !== null,
  );
  protected meterPoint = computed(() => this.wizardStore.state().meterPoint);
  protected meterPoints = computed<MeterPoint[]>(() => (this.meterPoint() ? [this.meterPoint()] : []));
  protected translateParams = computed<{ address: string }>(() => ({
    address: getAddressStreetAndPostCodeFormatted(this.meterPoint()?.adresse),
  }));

  hasAllNeededData(): boolean {
    closeIfInactive(this.wizardStore.state(), this.router);
    checkPrevDataAvailable(this.hasPrevStepData(), this.router, [ROUTE_METER_POINT_CHANGE_REQUEST, ROUTE_CHANGE_TYPE]);
    return true;
  }

  submit(): Promise<void> {
    const selectedProductsFormValue = this.form.get(SELECTED_METER_POINT_PRODUCTS).value;
    const meterPoints = Object.entries(selectedProductsFormValue).map(([id, inputValue]) =>
      this.getTrimmedInputs(
        inputValue,
        this.meterPoints().find(mp => mp.id === id),
      ),
    );
    this.wizardStore.patchState({
      productAndDate: {
        meterPoints,
      },
    });
    return Promise.resolve();
  }

  validate(): boolean {
    this.form.markAllAsTouched();
    this.form.updateValueAndValidity();
    return this.form?.valid;
  }

  private getTrimmedInputs(inputValues: any, meterpoint: MeterPoint): MeterPointSelection {
    return {
      id: meterpoint.id,
      category: meterpoint.sparte as ProductCategory,
      meterPointNumber: inputValues.meterPointNumber.trim(),
      meterNumber: inputValues.meterNumber.trim(),
      contractNumber: meterpoint.vertragsnummer,
      meterCount: inputValues.meterCount.trim(),
    };
  }
}
